import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class DarkModeService {
  private readonly storageKey = 'darkModeEnabled';

  constructor() { }

  /**
   * Enables dark mode by adding the `dark` class to the root HTML element.
   */
  enableDarkMode(): void {
    document.documentElement.classList.add('dark');
    this.setDarkModeState(true);
  }

  /**
   * Disables dark mode by removing the `dark` class from the root HTML element.
   */
  disableDarkMode(): void {
    document.documentElement.classList.remove('dark');
    this.setDarkModeState(false);
  }

  /**
   * Toggles the dark mode based on the current state.
   */
  toggleDarkMode(): void {
    if (this.isDarkModeEnabled()) {
      this.disableDarkMode();
    } else {
      this.enableDarkMode();
    }
  }

  /**
   * Checks if dark mode is enabled in local storage or falls back to the browser’s preference.
   * Defaults to checking `prefers-color-scheme` if no value is stored.
   *
   * @returns {boolean} True if dark mode should be enabled, false otherwise.
   */
  isDarkModeEnabled(): boolean {
    const savedState = localStorage.getItem(this.storageKey);

    if (savedState !== null) {
      // If a dark mode state exists in local storage, use it
      return savedState === 'true';
    } else {
      // Otherwise, check the browser’s preferred color scheme
      return this.prefersDarkMode();
    }
  }

  /**
   * Initializes dark mode based on the local storage setting or browser preference.
   */
  initializeDarkMode(): void {
    if (this.isDarkModeEnabled()) {
      this.enableDarkMode();
    } else {
      this.disableDarkMode();
    }
  }

  /**
   * Checks if the browser's preferred color scheme is dark.
   *
   * @returns {boolean} True if the browser prefers dark mode, false otherwise.
   */
  prefersDarkMode(): boolean {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  /**
   * Saves the dark mode state in local storage.
   *
   * @param isEnabled whether dark mode is enabled.
   */
  private setDarkModeState(isEnabled: boolean): void {
    localStorage.setItem(this.storageKey, isEnabled.toString());
  }
}
